import React, { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useGetPaymentsQuery } from "@/store/mover/apis";
import { Charge } from "@/types";
import { humanizeString, showCardDetails } from "@/lib/utils";
import { PAYMENT_PURPOSE_TYPES, FILTER_LABELS } from "@/constants";
import { Loading } from "@/components/Loading";
import DateFilterLinks, { FilterKey } from "@/components/DateFilterLinks";

const Payments: React.FC = () => {
  const [searchParams] = useSearchParams();
  const dateRangeFilter = searchParams.get("dateRangeFilter") ?? "today";
  const before = searchParams.get("before") ?? format(new Date(), "yyyy-MM-dd");
  const after = searchParams.get("after") ?? format(new Date(), "yyyy-MM-dd");

  const {
    data: payments,
    isLoading,
    isFetching,
    error,
  } = useGetPaymentsQuery({ before, after });

  const totalAmount = useMemo(
    () => payments?.reduce((sum, p) => sum + (Number(p.amount) || 0), 0) ?? 0,
    [payments]
  );
  const totalRefunded = useMemo(
    () =>
      payments?.reduce((sum, p) => sum + (Number(p.amount_refunded) || 0), 0) ??
      0,
    [payments]
  );
  const netAmount = useMemo(
    () => totalAmount - totalRefunded,
    [totalAmount, totalRefunded]
  );

  if (isLoading || isFetching)
    return (
      <div className="p-6 container mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Payments</h1>
        </div>
        <Loading />
      </div>
    );
  if (error)
    return (
      <div className="p-6 container mx-auto">
        Error loading payments data. Please try again later.
      </div>
    );

  const humanizePaymentMode = (mode: string): string => {
    return humanizeString(mode);
  };

  if (payments && payments.length === 0) {
    return (
      <div className="p-6 container mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Payments</h1>
        </div>
        <div className="flex flex-row justify-between items-end">
          <div>
            <h4 className="text-sm font-semibold">
              {FILTER_LABELS[dateRangeFilter as keyof typeof FILTER_LABELS]}
            </h4>
          </div>
          <div>
            <DateFilterLinks defaultKey={dateRangeFilter as FilterKey} />
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Refunded</TableHead>
              <TableHead>Purpose</TableHead>
              <TableHead>Method</TableHead>
              <TableHead>Source</TableHead>
            </TableRow>
          </TableHeader>
        </Table>
        <div className="text-sm mt-4 font-semibold">
          No payments were recorded in this time period
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Payments</h1>
          <h4 className="text-base text-gray-700">
            All your customer payments
          </h4>
        </div>
        <div className="flex space-x-8">
          <div className="flex flex-col items-start">
            <div className="text-sm font-medium text-gray-500">Payments</div>
            <div className="text-xl font-semibold">
              {Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format(totalAmount)}
            </div>
          </div>
          <div className="flex flex-col items-start">
            <div className="text-sm font-medium text-gray-500">Refunds</div>
            <div className="text-xl font-semibold text-red-600">
              {Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format(totalRefunded)}
            </div>
          </div>
          <div className="flex flex-col items-start">
            <div className="text-sm font-medium text-gray-500">Net</div>
            <div className="text-xl font-semibold">
              {Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format(netAmount)}
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className="flex flex-row justify-between items-end">
          <div>
            <h4 className="text-sm font-semibold">
              {FILTER_LABELS[dateRangeFilter as keyof typeof FILTER_LABELS]}
            </h4>
          </div>
          <div>
            <DateFilterLinks defaultKey={dateRangeFilter as FilterKey} />
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Refunded</TableHead>
              <TableHead>Purpose</TableHead>
              <TableHead>Method</TableHead>
              <TableHead>Source</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {payments && payments.length > 0 ? (
              payments.map((payment: Charge) => (
                <TableRow key={payment.id}>
                  <TableCell>
                    {format(new Date(payment.created_at), "M/d/yy hh:mm a")}
                  </TableCell>
                  <TableCell>
                    <Link
                      to={`/move/${payment.move?.id}`}
                      className="text-blue-600 hover:underline"
                      target="_blank"
                    >
                      {payment.move?.customer_name}
                    </Link>
                  </TableCell>
                  <TableCell>
                    {Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                    }).format(payment.amount || 0)}
                  </TableCell>
                  <TableCell>
                    {payment.amount_refunded > 0 ? (
                      <span className="text-red-600">
                        {Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: "USD",
                        }).format(payment.amount_refunded || 0)}
                      </span>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>
                    {
                      PAYMENT_PURPOSE_TYPES[
                        payment.purpose as keyof typeof PAYMENT_PURPOSE_TYPES
                      ]
                    }
                  </TableCell>
                  <TableCell>
                    {humanizePaymentMode(payment.mode_of_payment || "-")}
                    {payment.payment_method?.brand &&
                      payment.payment_method?.last_4 && (
                        <div className="text-xs text-gray-600">
                          {showCardDetails(payment.payment_method)}
                        </div>
                      )}
                  </TableCell>
                  <TableCell>
                    {payment.initiated_by === "customer" && <div>Customer</div>}
                    {payment.initiated_by === "mover" && (
                      <div>
                        <div>
                          {payment.user ? (
                            <div>{payment.user.name}</div>
                          ) : (
                            <div>Mover</div>
                          )}
                        </div>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-4">
                  No payments found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default Payments;
